import { create } from 'zustand';
import {
    ELEMENT_TYPES,
    createEmptyDocument,
    createElementByType,
    normalizeDocument,
    getSortedElements,
    pruneGroups,
    getGroupAncestry,
    getGroupElements,
    getGroupSubtreeIds,
    orderElementsByGroups,
    SIZE_PRESETS,
    createId,
    createFrame,
    withFrames,
    getFrames,
    frameById,
    frameForBox,
    sceneBounds,
    nextFrameOrigin,
    nextFrameName,
    isLockedFor,
    linkKind,
    contentLockKey,
} from '../schema/documentSchema';
import { isSectionLocked } from '../schema/sectionLocks';
import { clampTextSize, minTextBox } from '../utils/textMetrics';
import {
    applyFillToRange, remapColorSpans, rescaleColorSpans, replacedWholeText,
} from '../utils/textRuns';
import { getCrop, cropFromFit, cropForFitMode, naturalSize } from '../utils/imageCrop';
import { removeImageBackground } from '../utils/removeImageBackground';
import { optimizeImageSrc, targetSideFor } from '../utils/imageOptimize';
import {
    recolorElementPatch, recolorFramePatch, restoreElementPatch, restoreFramePatch,
} from '../utils/paletteRecolor';
import {
    newColorToken, replaceElementColorPatch, replaceFrameColorPatch,
    resetElementColorsPatch, resetFrameColorsPatch,
} from '../utils/colorUsage';
import { nodesToPathPatch, commandsToNodes, boxShapeNodes } from '../utils/vectorPath';
import {
    FREEFORM_SHAPE, isPathShapeElement, isFreeformElement, shapeCommands,
} from '../schema/shapeLibrary';

const MAX_HISTORY = 80;
const SNAP_THRESHOLD = 6;

/**
 * Apply a patch, carrying any per-character colours across a text change.
 *
 * Every route that edits text — typing on canvas, the panel's textarea, the Bijoy
 * repair, a paste — lands in updateElement, so remapping here is what keeps a
 * coloured word coloured instead of leaving the spans pointing at whatever
 * characters have since moved into those offsets. A patch that sets `colorSpans`
 * itself (the colour picker) is left alone.
 */

/**
 * Linked content — one piece of content shown in several places.
 *
 * A wedding card names the bride three times, in three different sizes and
 * colours. The admin links those three text layers into one group (`contentKey`)
 * so the person filling the card in types the name ONCE. Only the words travel:
 * every member keeps its own font, colour, size and position, because that is
 * the design and it is not the user's to change.
 *
 * Photos work the same way and for the same reason — an ID sheet shows the same
 * face at passport size, at stamp size and cropped square — so a photo group
 * shares the PICTURE and nothing else: each place keeps its own box, crop,
 * border and effects.
 *
 * A member can opt out (`contentDetached`) when one of the three really does
 * need to say something else — the arrow beside the field in the user panel.
 */
function linkedSiblings(elements, el) {
    if (!el?.contentKey || el.contentDetached) return [];
    return elements.filter((e) => (
        e.id !== el.id && e.contentKey === el.contentKey && !e.contentDetached
    ));
}

/** A grid copied deep — two tables sharing one `cells` array would alias it. */
function cloneCells(cells) {
    return Array.isArray(cells) ? cells.map((row) => (Array.isArray(row) ? [...row] : row)) : cells;
}

/**
 * The part of a patch that TRAVELS to the rest of a linked group — the shared
 * content only. A replacement photo takes the old crop with it (as null), since
 * a crop frames the picture it was cut from and means nothing for another one.
 *
 * A QR code shares BOTH halves of what it shows: the value it encodes and the
 * logo sitting in the middle of it. That is what "QR code with logo" is as a
 * block — a pair — and a group where the sheet's second QR keeps yesterday's
 * logo is the same bug linking exists to avoid. A table shares its grid.
 */
function linkedPayload(el, patch) {
    if (el?.type === ELEMENT_TYPES.TABLE) {
        if (!patch) return null;
        const out = {};
        if ('cells' in patch) out.cells = cloneCells(patch.cells);
        if ('rows' in patch) out.rows = patch.rows;
        if ('cols' in patch) out.cols = patch.cols;
        return Object.keys(out).length ? out : null;
    }
    if (el?.type === ELEMENT_TYPES.QRCODE) {
        const out = {};
        if (typeof patch?.content === 'string') out.content = patch.content;
        if (patch && 'src' in patch) out.src = patch.src;
        return Object.keys(out).length ? out : null;
    }
    if (typeof patch?.content === 'string') return { content: patch.content };
    if (el?.type === ELEMENT_TYPES.IMAGE && patch && 'src' in patch) {
        return { src: patch.src, ...('crop' in patch ? { crop: patch.crop } : null) };
    }
    return null;
}

/**
 * What of that payload this particular sibling can hold.
 *
 * A group is keyed by KIND, not by type, so a QR code and the heading it encodes
 * can share one field. A picture cannot: `src` on a text layer is nothing the
 * renderer draws, so the logo half travels only between layers of the same type.
 */
const TYPE_BOUND_SHARED_KEYS = ['src', 'crop', 'cells', 'rows', 'cols'];

function payloadForSibling(source, sibling, payload) {
    if (source.type === sibling.type) return payload;
    const portable = Object.fromEntries(
        Object.entries(payload).filter(([k]) => !TYPE_BOUND_SHARED_KEYS.includes(k)),
    );
    return Object.keys(portable).length ? portable : null;
}

/** Mirror a content edit onto the rest of its linked group. */
function spreadLinkedContent(elements, patches) {
    const extra = {};
    Object.entries(patches).forEach(([id, patch]) => {
        const source = elements.find((e) => e.id === id);
        const payload = linkedPayload(source, patch);
        if (!payload) return;
        linkedSiblings(elements, source).forEach((sib) => {
            if (patches[sib.id]) return; // an explicit edit wins over the mirror
            const forSib = payloadForSibling(source, sib, payload);
            if (!forSib) return;
            extra[sib.id] = {
                ...(extra[sib.id] || {}),
                ...forSib,
                ...('cells' in forSib ? { cells: cloneCells(forSib.cells) } : null),
            };
        });
    });
    return Object.keys(extra).length ? { ...patches, ...extra } : patches;
}

/** Does this patch write the thing a linked group shares? */
function writesSharedContent(el, patch) {
    if (!patch) return false;
    switch (el?.type) {
        case ELEMENT_TYPES.IMAGE: return 'src' in patch;
        case ELEMENT_TYPES.QRCODE: return 'content' in patch || 'src' in patch;
        case ELEMENT_TYPES.TABLE: return 'cells' in patch || 'rows' in patch || 'cols' in patch;
        default: return 'content' in patch;
    }
}

function withRemappedColorSpans(el, patch, memo) {
    const next = { ...el, ...patch };
    if (patch.content == null) return { el: next, memo };
    // The colour picker writes the spans itself; it owns them from here.
    if (patch.colorSpans !== undefined) return { el: next, memo: null };
    const before = String(el.content ?? '');
    const after = String(patch.content);
    if (after === before) return { el: next, memo };
    const spans = Array.isArray(el.colorSpans) && el.colorSpans.length ? el.colorSpans : null;

    // The runs as they stood when this burst of typing started. Held across every
    // keystroke of it, because backspacing a line away destroys them one run at a
    // time: by the time the box is empty there is nothing left to remember.
    const parked = memo || (spans ? { spans, length: before.length } : null);
    if (!parked) return { el: next, memo: null };

    // Retyping a line WHOLESALE — cleared and typed again, or replaced in one go —
    // leaves no character to inherit a colour from, and the text is far too short
    // to hold the runs while it is one letter long: remapping alone collapses a
    // two-colour line into one, and every later keystroke just extends that one
    // colour. Lay the parked runs back over the new wording instead, in the same
    // proportions, on every keystroke until the user moves on.
    const wiped = !after.length || replacedWholeText(before, after);
    if (wiped || parked.restoring) {
        next.colorSpans = after.length
            ? rescaleColorSpans(parked.spans, parked.length, after)
            : [];
        // `restoring` stays on for the rest of the burst: the second letter typed
        // into an emptied box is an ordinary-looking edit of a one-letter line, and
        // handing it back to the remapper is what re-flattened the colours.
        return { el: next, memo: { ...parked, restoring: true } };
    }
    // An ordinary edit — typing inside the words, appending, deleting a bit. The
    // runs still have characters to hold on to, so they keep their exact edges.
    if (spans) next.colorSpans = remapColorSpans(spans, before, after);
    return { el: next, memo: parked };
}

/**
 * A patch worth re-measuring text for: one that changes the box, or the styling
 * that decides how much room the text needs inside it. Text renders with
 * `overflow: hidden`, so a box below that is a box that eats glyphs — clampTextSize
 * grows it back. Anything else (move, rotate, colour…) skips the measurement.
 */
function sizesText(patch) {
    if (!patch) return false;
    if (patch.width != null || patch.height != null || patch.content != null) return true;
    const s = patch.styles;
    return !!s && (
        s.fontSize != null || s.fontFamily != null || s.fontWeight != null
        || s.fontStyle != null || s.lineHeight != null || s.letterSpacing != null
        || s.whiteSpace != null || s.textTransform != null || s.borderWidth != null
        || s.padding != null || s.paddingTop != null || s.paddingRight != null
        || s.paddingBottom != null || s.paddingLeft != null
    );
}

function clone(doc) {
    return JSON.parse(JSON.stringify(doc));
}

function pushHistory(state) {
    const past = [...state.past, clone(state.document)].slice(-MAX_HISTORY);
    return { past, future: [] };
}

/**
 * When copying/duplicating elements, groups represented by ≥2 items keep
 * their grouping under a fresh group id; lone members come out ungrouped.
 * Ancestors of a copied group are copied too, so nested sub-layers survive
 * (pruneGroups later dissolves any wrapper that ends up with <2 children).
 */
/**
 * @param all  copy EVERY group the items belong to, however few of them were
 *   taken. A frame duplicate wants this: its copy is a separate board, and a
 *   layer on it must not stay a member of a group living on the original.
 *   A layer duplicate does not — one member copied out of a group stays in that
 *   group (see the callers), which is what a layer list is expected to show.
 */
function remapGroupsForCopies(items = [], groups = [], { all = false } = {}) {
    const counts = {};
    items.forEach((el) => {
        if (el.groupId) counts[el.groupId] = (counts[el.groupId] || 0) + 1;
    });
    const remap = {};
    const newGroups = [];
    const byId = new Map((groups || []).map((g) => [g.id, g]));

    // Every group to copy = the multi-member ones plus their whole ancestry.
    const wanted = new Set();
    Object.entries(counts).forEach(([gid, count]) => {
        if (!all && count < 2) return;
        let cur = byId.get(gid);
        let guard = 0;
        while (cur && guard <= byId.size) { wanted.add(cur.id); cur = cur.parentId ? byId.get(cur.parentId) : null; guard += 1; }
    });
    wanted.forEach((gid) => { remap[gid] = createId('grp'); });
    wanted.forEach((gid) => {
        const source = byId.get(gid);
        newGroups.push({
            id: remap[gid],
            name: `${source?.name || 'Group'} copy`,
            parentId: source?.parentId ? (remap[source.parentId] || null) : null,
        });
    });
    return { remap, newGroups };
}

/**
 * Land copies where every design tool lands them: directly ABOVE the layer they
 * came from, inside the same group — not on top of the whole frame.
 *
 * The copies stay one contiguous block, placed after the topmost of their
 * sources, rather than each being slotted in above its own original. Interleaved
 * copies would split a duplicated GROUP into alternating members, and a group
 * whose members are not contiguous no longer reads as one block in the layers
 * tree (the same rule `orderElementsByGroups` enforces on load).
 *
 * Returns the full element list, re-indexed — `zIndex` IS the order here, so a
 * clean 0..n-1 run is what keeps it that way.
 */
function insertClonesAbove(elements, originals, clones) {
    const sorted = getSortedElements(elements);
    const sourceIds = new Set(originals.map((el) => el.id));
    let topmost = -1;
    sorted.forEach((el, i) => { if (sourceIds.has(el.id)) topmost = i; });
    const out = [...sorted];
    out.splice(topmost + 1, 0, ...clones);
    return out.map((el, i) => ({ ...el, zIndex: i }));
}

/* ------------------------------------------------------------------ *
 *  System-clipboard codec
 *
 *  ⌘C also writes the copied elements to the system clipboard as JSON, so a
 *  paste still works after a reload or in a second tab. The in-app `clipboard`
 *  array stays the fast path; this is only consulted when it disagrees.
 * ------------------------------------------------------------------ */

const CLIPBOARD_MARKER = '__printTemplateClipboard';

function encodeClipboard(elements) {
    return JSON.stringify({ [CLIPBOARD_MARKER]: 1, elements });
}

function decodeClipboard(text) {
    const raw = String(text || '').trim();
    // Cheap prefilter — the payload can be megabytes once images are embedded,
    // and arbitrary pasted text should never reach JSON.parse.
    if (raw[0] !== '{' || !raw.slice(0, 200).includes(CLIPBOARD_MARKER)) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed?.[CLIPBOARD_MARKER] || !Array.isArray(parsed.elements)) return null;
        const elements = parsed.elements.filter((el) => el && typeof el === 'object' && el.type);
        return elements.length ? elements : null;
    } catch {
        return null;
    }
}

/** Same source elements? Copies get fresh ids, so the originals' ids identify the set. */
function sameClipboardSource(a = [], b = []) {
    return a.length === b.length && a.every((el, i) => el.id === b[i]?.id);
}

/**
 * Re-home an element on the frame holding its centre, whenever a patch moved or
 * resized it. Dragging a design from one frame onto another therefore moves it
 * between exports, with no extra gesture — and `frameForBox` keeps the current
 * frame when the element is parked on empty scene, so nothing becomes an orphan
 * that no export would render.
 */
function reframe(frames, el, patch) {
    if (!patch || (patch.x == null && patch.y == null
        && patch.width == null && patch.height == null)) return el;
    const frameId = frameForBox(frames, el, el.frameId);
    return frameId === el.frameId ? el : { ...el, frameId };
}

export const usePrintEditorStore = create((set, get) => ({
    document: createEmptyDocument(),
    selectedIds: [],
    /** The selected FRAME, if any. Frames and elements are never selected together. */
    selectedFrameId: null,
    /** Group the user has "entered" via double-click — clicks inside select children directly. */
    enteredGroupId: null,
    /** Image element being cropped (double-click / the panel's crop button), else null. */
    croppingId: null,
    /**
     * Path element whose ANCHORS are being edited — Figma's direct-selection /
     * Illustrator's white arrow. Entered by double-clicking a shape or a drawn
     * path; the canvas then shows its nodes and handles instead of resize grips.
     */
    editingPathId: null,
    /**
     * Crop rect for an element that has none stored yet — seeded from its fit mode so
     * the overlay opens on exactly what is on screen. Geometry only: it reaches the
     * document on the first crop edit, so opening crop mode and backing out changes
     * nothing and dirties nothing.
     */
    cropSeed: null,
    /**
     * The characters selected inside the text element currently being edited on
     * canvas: `{ id, start, end }`, else null. Lives here rather than in the
     * canvas because the properties panel is what acts on it — picking a colour
     * with part of a run selected colours those characters only.
     */
    textSelection: null,
    /**
     * Per-character colours parked while a text layer is being RETYPED from
     * scratch: `{ [elementId]: { spans, length } }`.
     *
     * Deliberately not in the document. A line cleared to nothing, or replaced in
     * one go, has no character left to carry its runs and no room to hold them
     * while it is one letter long — so they wait here and are re-laid over the new
     * wording as it is typed, and are dropped the moment the user selects
     * something else or picks a colour by hand.
     */
    _spanMemo: {},
    zoom: 1,
    panX: 0,
    panY: 0,
    tool: 'select', // select | pan | text | ...
    snapEnabled: true,
    showRulers: true,
    showGuides: true,
    mode: 'admin', // admin | user
    past: [],
    future: [],
    dirty: false,
    documentRevision: 0, // bump on load so viewport can re-fit
    clipboard: [], // copied elements (in-app clipboard)
    pasteCount: 0,
    // Background removals in flight, by element id: { busy, pct, error }. Here
    // rather than in the panel so the progress survives selecting another layer.
    bgRemoval: {},
    /** Image optimisation, per element: {busy, done, error, before, after, …}. */
    imageOptimize: {},
    /**
     * The picture as it was before it was optimised, per element — the way back.
     * Off the document on purpose: it is an undo, not a second copy to save.
     */
    imageOriginals: {},
    isExporting: false,
    _exportViewRestore: null,
    /** When true, updates mutate the document without pushing new undo steps. */
    _historyGesture: false,
    _historyGestureBase: null,

    /** Freeze viewport at 1:1 (no zoom/pan/clipping) for a faithful html2canvas capture. */
    beginExportCapture() {
        const { zoom, panX, panY, selectedIds, isExporting } = get();
        if (isExporting) return;
        set({
            isExporting: true,
            selectedIds: [],
            _exportViewRestore: { zoom, panX, panY, selectedIds: [...selectedIds] },
            zoom: 1,
            panX: 0,
            panY: 0,
        });
    },

    endExportCapture() {
        const restore = get()._exportViewRestore;
        if (!restore) {
            set({ isExporting: false, _exportViewRestore: null });
            return;
        }
        set({
            isExporting: false,
            _exportViewRestore: null,
            zoom: restore.zoom,
            panX: restore.panX,
            panY: restore.panY,
            selectedIds: restore.selectedIds || [],
        });
    },

    loadDocument(raw, { mode = 'admin' } = {}) {
        let source = raw;
        if (typeof source === 'string') {
            try { source = JSON.parse(source); } catch { source = null; }
        }
        // Deep clone so later store edits cannot mutate React props / API payload references
        if (source && typeof source === 'object') {
            try { source = JSON.parse(JSON.stringify(source)); } catch { /* keep as-is */ }
        }
        set((s) => ({
            document: normalizeDocument(source || createEmptyDocument()),
            selectedIds: [],
            selectedFrameId: null,
            enteredGroupId: null,
            croppingId: null,
            cropSeed: null,
            past: [],
            future: [],
            dirty: false,
            mode,
            zoom: 1,
            panX: 0,
            panY: 0,
            tool: 'select',
            documentRevision: (s.documentRevision || 0) + 1,
        }));
    },

    /**
     * Switch the editing mode WITHOUT reloading the document.
     *
     * `loadDocument` also sets the mode, but it throws the document, the history
     * and the viewport away with it — right for opening a template, wrong for the
     * admin's "User's POV" tab, which shows the very artwork being worked on as
     * the person filling it in would meet it. Selection, group entry and crop are
     * dropped because they are admin-side state the other mode cannot express;
     * `dirty` is deliberately untouched — looking is not editing.
     */
    setMode(next) {
        const mode = next === 'user' ? 'user' : 'admin';
        if (get().mode === mode) return;
        set({
            mode,
            selectedIds: [],
            selectedFrameId: null,
            enteredGroupId: null,
            croppingId: null,
            cropSeed: null,
            // Anchor editing is an authoring mode; leaving admin leaves it too.
            editingPathId: null,
            textSelection: null,
            tool: 'select',
        });
    },

    reset() {
        set((s) => ({
            document: createEmptyDocument(),
            selectedIds: [],
            selectedFrameId: null,
            enteredGroupId: null,
            croppingId: null,
            cropSeed: null,
            past: [],
            future: [],
            dirty: false,
            zoom: 1,
            panX: 0,
            panY: 0,
            documentRevision: (s.documentRevision || 0) + 1,
        }));
    },

    /* ------------------------------------------------------------------ *
     *  Frames (artboards)
     * ------------------------------------------------------------------ */

    /**
     * The frame new elements land in: the one selected, else the frame of the
     * selected element, else the first. Never null while a document is loaded.
     */
    activeFrame() {
        const state = get();
        const frames = getFrames(state.document);
        return frameById(state.document, state.selectedFrameId)
            || frames.find((f) => f.id === state.document.elements
                .find((el) => el.id === state.selectedIds[0])?.frameId)
            || frames[0]
            || null;
    },

    /**
     * The frame an insert should land on, creating one if the document has none.
     * Deleting every frame is allowed, so this is what stops the next insert from
     * producing content that belongs to no artboard and would never export.
     */
    ensureFrame() {
        const existing = get().activeFrame();
        if (existing) return existing;
        return get().addFrame();
    },

    selectFrame(frameId) {
        // A frame and elements are never selected together — the properties panel
        // shows one or the other. Selecting a frame also ends crop mode, since the
        // picture being cropped is no longer the selection.
        set({
            selectedFrameId: frameId || null,
            selectedIds: [],
            enteredGroupId: null,
            croppingId: null,
            cropSeed: null,
        });
    },

    /** `skipHistory` batches a continuous gesture (e.g. dragging a frame edge) into
     *  a single undo step — the caller commits one snapshot when the gesture ends. */
    setFrame(frameId, partial, { skipHistory = false } = {}) {
        const state = get();
        const target = frameById(state.document, frameId) || getFrames(state.document)[0];
        if (!target) return;
        const frames = getFrames(state.document).map((f) => {
            if (f.id !== target.id) return f;
            const next = { ...f, ...partial };
            // Keep presetId in sync when width/height change, unless set explicitly
            if (partial.presetId == null && (partial.width != null || partial.height != null)) {
                const match = SIZE_PRESETS.find(
                    (p) => p.id !== 'custom' && p.width === next.width && p.height === next.height
                );
                next.presetId = match?.id || 'custom';
            }
            return next;
        });
        // A frame that grew now covers elements that used to sit outside it (and one
        // that shrank lost some), so re-home everything against the new geometry.
        const elements = state.document.elements.map((el) => (
            { ...el, frameId: frameForBox(frames, el, el.frameId) }
        ));
        const document = withFrames({ ...state.document, elements }, frames);
        if (skipHistory || state._historyGesture) set({ document, dirty: true });
        else set({ document, dirty: true, ...pushHistory(state) });
    },

    /** Legacy alias — patches the active frame. Kept for callers not yet migrated. */
    setCanvas(partial, opts) {
        get().setFrame(get().activeFrame()?.id, partial, opts);
    },

    /**
     * Patch SEVERAL frames in one undo step — what the colour palettes do when the
     * scope is "all frames". Going through setFrame per frame would leave one undo
     * entry per artboard, so a single palette click would need five undos.
     */
    setFramesFill(frameIds = [], patch = {}, { skipHistory = false } = {}) {
        const state = get();
        const ids = new Set(frameIds);
        if (!ids.size) return;
        const frames = getFrames(state.document).map((f) => (
            ids.has(f.id) ? { ...f, ...patch } : f
        ));
        const document = withFrames(state.document, frames);
        if (skipHistory || state._historyGesture) set({ document, dirty: true });
        else set({ document, dirty: true, ...pushHistory(state) });
    },

    /**
     * Add a frame. Placed to the right of every existing frame by default, so a new
     * artboard never lands on top of a design, and selected so the panel is already
     * showing its size preset.
     */
    addFrame(overrides = {}) {
        const state = get();
        const frames = getFrames(state.document);
        const base = state.activeFrame() || frames[0];
        const width = overrides.width ?? base?.width ?? 1050;
        const height = overrides.height ?? base?.height ?? 600;
        const origin = nextFrameOrigin(frames, width);
        const frame = createFrame({
            name: nextFrameName(frames),
            // A new frame inherits the current one's size preset — the usual case is
            // a second page of the same product.
            width,
            height,
            background: overrides.background ?? base?.background ?? '#ffffff',
            presetId: overrides.presetId ?? base?.presetId,
            x: overrides.x ?? origin.x,
            y: overrides.y ?? origin.y,
        });
        set({
            document: withFrames(state.document, [...frames, frame]),
            selectedFrameId: frame.id,
            selectedIds: [],
            enteredGroupId: null,
            dirty: true,
            tool: 'select',
            ...pushHistory(state),
        });
        return frame;
    },

    /**
     * Copy a frame AND everything on it, side by side with the original.
     *
     * `at` overrides where the copy lands — ⌥-dragging a frame label drops it exactly
     * on the original and lets the drag carry it away, the way Illustrator and Figma
     * do; `skipHistory` keeps that whole gesture as ONE undo step.
     */
    duplicateFrame(frameId, { at = null, skipHistory = false } = {}) {
        const state = get();
        const frames = getFrames(state.document);
        const source = frameById(state.document, frameId) || state.activeFrame();
        if (!source) return null;
        const origin = at || nextFrameOrigin(frames, source.width);
        const copy = {
            ...clone(source),
            id: createId('frame'),
            name: `${source.name} copy`,
            x: origin.x,
            y: origin.y,
        };
        const dx = copy.x - source.x;
        const dy = copy.y - source.y;
        const members = getSortedElements(state.document.elements)
            .filter((el) => el.frameId === source.id);
        // A frame copy takes EVERY group with it, one-member groups included —
        // otherwise a lone grouped layer would land on the new board still listed
        // as a member of a group back on the old one.
        const { remap, newGroups } = remapGroupsForCopies(members, state.document.groups, { all: true });
        const maxZ = Math.max(0, ...state.document.elements.map((e) => e.zIndex ?? 0));
        const clones = members.map((el, i) => ({
            ...clone(el),
            id: createId('el'),
            frameId: copy.id,
            x: el.x + dx,
            y: el.y + dy,
            zIndex: maxZ + 1 + i,
            groupId: remap[el.groupId] || null,
        }));
        set({
            document: withFrames({
                ...state.document,
                elements: [...state.document.elements, ...clones],
                groups: [...(state.document.groups || []), ...newGroups],
            }, [...frames, copy]),
            selectedFrameId: copy.id,
            selectedIds: [],
            dirty: true,
            ...(skipHistory ? null : pushHistory(state)),
        });
        return copy;
    },

    /**
     * Delete a frame and everything on it — including the last one. A document with
     * no frames is a legitimate blank slate: the next insert (or the Frame tool)
     * creates one, so nothing can be added that would never export.
     */
    deleteFrame(frameId) {
        const state = get();
        const frames = getFrames(state.document);
        const target = frameById(state.document, frameId);
        if (!target) return;
        const rest = frames.filter((f) => f.id !== target.id);
        const elements = state.document.elements.filter((el) => el.frameId !== target.id);
        const pruned = pruneGroups(state.document.groups || [], elements);
        set({
            document: withFrames({
                ...state.document,
                elements: pruned.elements,
                groups: pruned.groups,
            }, rest),
            selectedFrameId: null,
            selectedIds: [],
            enteredGroupId: null,
            dirty: true,
            ...pushHistory(state),
        });
    },

    /**
     * Move a frame on the scene, carrying its contents. Elements hold scene
     * coordinates, so they have to shift with the frame or the whole design would
     * be left behind.
     *
     * `skipHistory` batches a drag into one undo step, committed on pointerup.
     */
    moveFrame(frameId, dx, dy, { skipHistory = false } = {}) {
        const state = get();
        const target = frameById(state.document, frameId);
        if (!target || (!dx && !dy)) return;
        const frames = getFrames(state.document).map((f) => (
            f.id === target.id ? { ...f, x: Math.round(f.x + dx), y: Math.round(f.y + dy) } : f
        ));
        const elements = state.document.elements.map((el) => (
            el.frameId === target.id
                ? { ...el, x: Math.round(el.x + dx), y: Math.round(el.y + dy) }
                : el
        ));
        const document = withFrames({ ...state.document, elements }, frames);
        if (skipHistory || state._historyGesture) set({ document, dirty: true });
        else set({ document, dirty: true, ...pushHistory(state) });
    },

    /** Put the frame at an exact scene position (the panel's X / Y inputs). */
    placeFrame(frameId, { x, y }) {
        const target = frameById(get().document, frameId);
        if (!target) return;
        get().moveFrame(
            frameId,
            x == null ? 0 : Math.round(x) - target.x,
            y == null ? 0 : Math.round(y) - target.y,
        );
    },

    /**
     * Recolour whole frames onto a palette — the Colour palettes tab.
     *
     * Every colour in scope is mapped onto the palette's light→dark ramp by its own
     * lightness (see utils/paletteRecolor), so the design actually becomes the
     * colour that was clicked. The original colours are snapshotted on first use and
     * every later palette is applied to that snapshot, so switching palettes never
     * compounds and `clearPalette` restores exactly what was drawn.
     */
    applyPalette(frameIds = [], palette) {
        const ramp = palette?.colors || [];
        const ids = new Set(frameIds);
        if (!ramp.length || !ids.size) return;
        const state = get();

        const frames = getFrames(state.document).map((f) => {
            if (!ids.has(f.id)) return f;
            const patch = recolorFramePatch(f, ramp);
            return patch ? { ...f, ...patch, paletteId: palette.id || null } : f;
        });
        const elements = state.document.elements.map((el) => {
            if (!ids.has(el.frameId)) return el;
            // `paletteId` is what makes IMAGES follow the palette: their pixels are
            // remapped at render time from this id (utils/paletteImage), so the saved
            // template keeps its original artwork and its R2 media keys.
            const tagged = { ...el, paletteId: palette.id || null };
            const patch = recolorElementPatch(el, ramp);
            if (!patch) return tagged;
            return { ...tagged, ...patch, styles: { ...(el.styles || {}), ...patch.styles } };
        });

        set({
            document: withFrames({ ...state.document, elements }, frames),
            dirty: true,
            ...pushHistory(state),
        });
    },

    /**
     * Swap ONE listed colour everywhere in scope — the Selection colour presets.
     *
     * `rowId` identifies the global being edited rather than a colour value, so
     * two globals that happen to hold the same colour stay two globals: the swap
     * stamps `token` onto every occurrence it touches, and from then on that is
     * what the row is. See utils/colorUsage for the identity rules.
     *
     * `paletteBase` is deliberately left alone. It holds the ORIGINAL artwork,
     * and Reset in the Palettes section promises exactly that — so a swap made
     * on top of a palette lives until the palette is switched or reset.
     */
    replaceColor(frameIds = [], rowId, toColor, token) {
        const ids = new Set(frameIds);
        if (!rowId || !ids.size || !toColor) return;
        const state = get();
        const stamp = token || newColorToken();

        const frames = getFrames(state.document).map((f) => {
            if (!ids.has(f.id)) return f;
            const patch = replaceFrameColorPatch(f, rowId, toColor, stamp);
            return patch ? { ...f, ...patch } : f;
        });
        const elements = state.document.elements.map((el) => {
            if (!ids.has(el.frameId)) return el;
            if (state.mode === 'user' && (el.locked || el.editableByUser === false)) return el;
            const patch = replaceElementColorPatch(el, rowId, toColor, stamp);
            if (!patch) return el;
            return {
                ...el,
                ...patch,
                ...(patch.styles ? { styles: { ...(el.styles || {}), ...patch.styles } } : null),
            };
        });

        const document = withFrames({ ...state.document, elements }, frames);
        // Dragging the picker is one gesture, not one undo entry per frame.
        if (state._historyGesture) set({ document, dirty: true });
        else set({ document, dirty: true, ...pushHistory(state) });
    },

    /**
     * Undo every Selection colour presets swap in scope.
     *
     * The originals were snapshotted the first time each place was recoloured
     * (`presetBase`), so this is exact however many times a colour was dragged
     * around after that. The identities go with them: a reset list is back to
     * plain "these are the colours the artwork uses".
     */
    resetColorPresets(frameIds = []) {
        const ids = new Set(frameIds);
        if (!ids.size) return;
        const state = get();

        let touched = false;
        const frames = getFrames(state.document).map((f) => {
            if (!ids.has(f.id)) return f;
            const patch = resetFrameColorsPatch(f);
            if (!patch) return f;
            touched = true;
            return { ...f, ...patch };
        });
        const elements = state.document.elements.map((el) => {
            if (!ids.has(el.frameId)) return el;
            if (state.mode === 'user' && (el.locked || el.editableByUser === false)) return el;
            const patch = resetElementColorsPatch(el);
            if (!patch) return el;
            touched = true;
            return {
                ...el,
                ...patch,
                ...(patch.styles ? { styles: { ...(el.styles || {}), ...patch.styles } } : null),
            };
        });
        if (!touched) return;

        set({
            document: withFrames({ ...state.document, elements }, frames),
            dirty: true,
            ...pushHistory(state),
        });
    },

    /** Put the designer's own colours back and forget the palette. */
    clearPalette(frameIds = []) {
        const ids = new Set(frameIds);
        if (!ids.size) return;
        const state = get();
        const frames = getFrames(state.document).map((f) => {
            if (!ids.has(f.id)) return f;
            const patch = restoreFramePatch(f);
            return patch ? { ...f, ...patch } : { ...f, paletteId: null };
        });
        const elements = state.document.elements.map((el) => {
            if (!ids.has(el.frameId)) return el;
            const cleared = { ...el, paletteId: null };
            const patch = restoreElementPatch(el);
            if (!patch) return cleared;
            return { ...cleared, ...patch, styles: { ...(el.styles || {}), ...patch.styles } };
        });
        set({
            document: withFrames({ ...state.document, elements }, frames),
            dirty: true,
            ...pushHistory(state),
        });
    },

    renameFrame(frameId, name) {
        const state = get();
        const clean = String(name || '').trim();
        if (!clean) return;
        const frames = getFrames(state.document).map((f) => (
            f.id === frameId ? { ...f, name: clean } : f
        ));
        set({
            document: withFrames(state.document, frames),
            dirty: true,
            ...pushHistory(state),
        });
    },

    setTool(tool) {
        set({ tool });
    },

    setZoom(zoom) {
        set({ zoom: Math.min(8, Math.max(0.1, zoom)) });
    },

    /** Zoom toward a viewport point (Figma-style). originX/Y = pointer relative to viewport. */
    zoomAt(nextZoom, originX, originY) {
        const { zoom, panX, panY } = get();
        const z = Math.min(8, Math.max(0.1, nextZoom));
        if (z === zoom) return;
        // Keep the world point under the cursor stable
        const worldX = (originX - panX) / zoom;
        const worldY = (originY - panY) / zoom;
        set({
            zoom: z,
            panX: originX - worldX * z,
            panY: originY - worldY * z,
        });
    },

    setPan(panX, panY) {
        set({ panX, panY });
    },

    /**
     * Fit EVERY frame in the viewport, centred. With one frame this is the old
     * "centre the artboard"; with several it frames the whole scene, so adding a
     * frame off to the right and hitting ⌘0 brings it into view.
     */
    fitToView(viewportWidth, viewportHeight, { padding = 64 } = {}) {
        const { document: doc } = get();
        const b = sceneBounds(doc);
        const w = Math.max(1, b.maxX - b.minX);
        const h = Math.max(1, b.maxY - b.minY);
        const availW = Math.max(100, viewportWidth - padding * 2);
        const availH = Math.max(100, viewportHeight - padding * 2);
        const z = Math.min(availW / w, availH / h, 1);
        const clamped = Math.min(8, Math.max(0.1, z));
        set({
            zoom: clamped,
            // Pan is in screen pixels, so the scene origin has to be scaled out too —
            // frames rarely start at 0,0 once there is more than one.
            panX: (viewportWidth - w * clamped) / 2 - b.minX * clamped,
            panY: (viewportHeight - h * clamped) / 2 - b.minY * clamped,
        });
    },

    toggleSnap() {
        set((s) => ({ snapEnabled: !s.snapEnabled }));
    },

    select(ids) {
        const list = Array.isArray(ids) ? ids : ids ? [ids] : [];
        // Selecting anything else leaves crop mode — cropping is a mode ON one picture.
        set((s) => ({
            selectedIds: list,
            selectedFrameId: null,
            // A character range survives the text editor closing — clicking a colour
            // in the panel blurs the textarea, and the range has to outlive that or
            // nothing could ever be coloured. It does NOT survive a new selection
            // gesture: picking any element (this one included) is how you say "back
            // to the whole element".
            textSelection: null,
            // Retyping is over once the user picks something else: the runs parked
            // for that layer are what it says now, not something still to restore.
            ...(Object.keys(s._spanMemo || {}).length ? { _spanMemo: {} } : {}),
            // Node editing is a mode ON one path, like cropping is on one picture.
            ...(s.editingPathId && !(list.length === 1 && list[0] === s.editingPathId)
                ? { editingPathId: null } : {}),
            ...(s.croppingId && !(list.length === 1 && list[0] === s.croppingId)
                ? { croppingId: null, cropSeed: null }
                : null),
        }));
    },

    clearSelection() {
        set({
            selectedIds: [],
            selectedFrameId: null,
            enteredGroupId: null,
            editingPathId: null,
            croppingId: null,
            cropSeed: null,
            textSelection: null,
        });
    },

    /** Report the caret / selection inside the text element being edited. */
    setTextSelection(sel) {
        set({
            textSelection: sel && sel.id != null && sel.end > sel.start
                ? { id: sel.id, start: sel.start, end: sel.end }
                : null,
        });
    },

    /**
     * Paint the characters in [start, end) of one text element — the panel's fill
     * editor while a run is selected. `paint` is {color, fill?} exactly as a span
     * stores it; null hands the characters back to the element's own fill.
     */
    setTextRangeFill(id, start, end, paint) {
        const el = get().document.elements.find((e) => e.id === id);
        if (!el) return;
        const text = String(el.content ?? '');
        get().updateElement(id, {
            colorSpans: applyFillToRange(el.colorSpans, start, end, paint, text.length),
        });
    },

    /* ------------------------------------------------------------------ *
     *  Image crop (Figma-style)
     * ------------------------------------------------------------------ */

    /**
     * Enter crop mode on an image: the canvas swaps its resize handles for crop
     * handles, and the whole picture shows dimmed around the frame.
     *
     * An element with no crop yet gets one seeded from its `fit` mode, so nothing
     * moves as the overlay opens. The seed lives in `cropSeed` rather than in the
     * document until the first edit — opening crop mode is not an edit.
     */
    async startCrop(id) {
        const state = get();
        const el = state.document.elements.find((e) => e.id === id);
        if (!el || el.type !== ELEMENT_TYPES.IMAGE || !el.src) return;
        // Cropping drags the picture and its box around, so either side's lock
        // stops it — the user lock for a user, the admin lock for an admin.
        if (isLockedFor(el, state.mode)) return;
        // Cropping is the Image section, done with the pointer.
        if (isSectionLocked(el, 'image', state.mode)) return;
        if (state.mode === 'user' && el.editableByUser === false) return;
        if (getCrop(el)) {
            set({ croppingId: id, cropSeed: null, selectedIds: [id], selectedFrameId: null });
            return;
        }
        const natural = await naturalSize(el.src);
        // The element can go (or change) while the picture's size is being read.
        const live = get().document.elements.find((e) => e.id === id);
        if (!live || live.src !== el.src) return;
        set({
            croppingId: id,
            cropSeed: getCrop(live) || cropFromFit(live, natural),
            selectedIds: [id],
            selectedFrameId: null,
        });
    },

    stopCrop() {
        set({ croppingId: null, cropSeed: null });
    },

    /**
     * Start editing a path's anchors. A CATALOGUE shape (star, arrow, …) is drawn
     * from a formula, so it has no nodes to drag: entering node edit converts it
     * to a freeform path with the very same outline — Figma's "flatten", done at
     * the moment it becomes necessary rather than as a separate chore.
     */
    startPathEdit(id = get().selectedIds[0]) {
        const state = get();
        if (state.mode !== 'admin') return false;
        const el = state.document.elements.find((e) => e.id === id);
        if (!el || el.type !== ELEMENT_TYPES.SHAPE || isLockedFor(el, state.mode)) return false;
        if (!isFreeformElement(el)) {
            const w = Math.max(1, Number(el.width) || 1);
            const h = Math.max(1, Number(el.height) || 1);
            // A star or an arrow is drawn from a formula, a rectangle from its own
            // box — either way the outline it already shows becomes its nodes.
            const flat = isPathShapeElement(el)
                ? commandsToNodes(shapeCommands(el, w, h), w, h)
                : boxShapeNodes(el.shape);
            if (!flat || flat.nodes.length < 2) return false;
            get().updateElement(id, { shape: FREEFORM_SHAPE, path: { closed: flat.closed, nodes: flat.nodes } });
        }
        set({ editingPathId: id, selectedIds: [id], selectedFrameId: null, croppingId: null });
        return true;
    },

    stopPathEdit() {
        set({ editingPathId: null });
    },

    /**
     * Write an edited outline back. `nodes` are SCENE points, so the box is
     * re-fitted to them — an anchor dragged outside the old box takes the box
     * with it, exactly as it does while the path is being drawn.
     */
    setPathNodes(id, nodes, closed, { skipHistory = false } = {}) {
        const patch = nodesToPathPatch(nodes, closed);
        if (!patch) return;
        get().updateElement(id, patch, { skipHistory });
    },

    /** Re-fit the picture inside the frame without leaving crop mode. */
    async recrop(id, fitMode) {
        const state = get();
        const el = state.document.elements.find((e) => e.id === id);
        if (!el || el.type !== ELEMENT_TYPES.IMAGE || !el.src) return;
        const natural = await naturalSize(el.src);
        const crop = cropForFitMode(el, natural, fitMode);
        get().updateElement(id, { crop });
        set((s) => (s.croppingId === id ? { cropSeed: null } : {}));
    },

    /** Drop the crop — the image goes back to plain `fit` + `objectPosition`. */
    clearCrop(id) {
        const el = get().document.elements.find((e) => e.id === id);
        if (!el) return;
        get().updateElement(id, { crop: null });
        set((s) => (s.croppingId === id ? { croppingId: null, cropSeed: null } : {}));
    },

    /* ------------------------------------------------------------------ *
     *  Layer groups (Figma-style)
     * ------------------------------------------------------------------ */

    /** Set / clear the group whose children are directly selectable. */
    enterGroup(groupId) {
        set({ enteredGroupId: groupId || null });
    },

    /**
     * What a canvas click on `elId` should select: the OUTERMOST group it belongs
     * to — or, once an ancestor has been entered (double-click), the next level
     * down inside it, so nested sub-layers are drilled one step at a time.
     * Applies in both modes so a group an admin created still behaves as one
     * solid unit for end users.
     */
    resolveSelectionTarget(elId) {
        const state = get();
        const { elements, groups } = state.document;
        const el = elements.find((e) => e.id === elId);
        if (!el) return [elId];
        const chain = getGroupAncestry(groups || [], el.groupId); // outermost → innermost
        if (!chain.length) return [elId];

        let target;
        const i = state.enteredGroupId ? chain.findIndex((g) => g.id === state.enteredGroupId) : -1;
        if (i < 0) target = chain[0];                 // not inside this stack → whole outer group
        else if (i === chain.length - 1) return [elId]; // inside the innermost → the element itself
        else target = chain[i + 1];                    // one level deeper

        return getGroupElements(elements, groups || [], target.id).map((e) => e.id);
    },

    /** The group a double-click should step into, given what's already entered. */
    resolveEnterTarget(elId) {
        const state = get();
        const el = state.document.elements.find((e) => e.id === elId);
        if (!el) return null;
        const chain = getGroupAncestry(state.document.groups || [], el.groupId);
        if (!chain.length) return null;
        const i = state.enteredGroupId ? chain.findIndex((g) => g.id === state.enteredGroupId) : -1;
        return chain[Math.min(i + 1, chain.length - 1)].id;
    },

    /**
     * Group the current selection (≥2 elements). Members become contiguous in
     * z-order. `name` overrides the default "Group N" — masking uses it.
     */
    groupSelected(idList = get().selectedIds, { name = '' } = {}) {
        const state = get();
        if (state.mode === 'user') return null;
        const ids = new Set(idList);
        if (ids.size < 2) return null;
        const sorted = getSortedElements(state.document.elements);
        const orderedMembers = sorted.filter((e) => ids.has(e.id));
        if (orderedMembers.length < 2) return null;

        // Nest the new group inside the deepest group that already contains the
        // whole selection (longest common ancestry prefix) — so grouping inside
        // an existing layer creates a SUB-layer instead of jumping to top level.
        const allGroups = state.document.groups || [];
        const chains = orderedMembers.map((e) => getGroupAncestry(allGroups, e.groupId).map((g) => g.id));
        let commonParent = null;
        if (chains.length) {
            for (let d = 0; d < chains[0].length; d += 1) {
                const id = chains[0][d];
                if (chains.every((c) => c[d] === id)) commonParent = id;
                else break;
            }
        }

        const group = {
            id: createId('grp'),
            name: name || `Group ${allGroups.length + 1}`,
            parentId: commonParent,
        };
        // Move the member block to the front-most member's position (Figma behavior)
        const frontId = orderedMembers[orderedMembers.length - 1].id;
        const newOrder = [];
        sorted.forEach((el) => {
            if (ids.has(el.id)) {
                if (el.id === frontId) newOrder.push(...orderedMembers);
                return;
            }
            newOrder.push(el);
        });
        const elements = newOrder.map((el, i) => ({
            ...el,
            zIndex: i,
            groupId: ids.has(el.id) ? group.id : (el.groupId || null),
        }));
        const pruned = pruneGroups([...allGroups, group], elements);
        set({
            document: {
                ...state.document,
                groups: pruned.groups,
                // Re-flatten so the new sub-group stays contiguous inside its parent too
                elements: orderElementsByGroups(pruned.elements, pruned.groups)
                    .map((el, i) => ({ ...el, zIndex: i })),
            },
            selectedIds: orderedMembers.map((e) => e.id),
            enteredGroupId: null,
            dirty: true,
            ...pushHistory(state),
        });
        return group.id;
    },

    /**
     * Dissolve one level of grouping for the selection. For each selected element
     * the OUTERMOST group whose members are all selected is removed (so selecting
     * a whole layer and hitting ⌘⇧G unwraps that layer); anything it held moves up
     * to its parent, keeping deeper sub-layers intact — one level at a time.
     */
    ungroupSelected() {
        const state = get();
        if (state.mode === 'user') return;
        const { elements: allEls, groups: allGroups = [] } = state.document;
        const ids = new Set(state.selectedIds);

        const gids = new Set();
        allEls.forEach((el) => {
            if (!ids.has(el.id) || !el.groupId) return;
            const chain = getGroupAncestry(allGroups, el.groupId); // outermost → innermost
            const fully = chain.find((g) => (
                getGroupElements(allEls, allGroups, g.id).every((m) => ids.has(m.id))
            ));
            gids.add((fully || chain[chain.length - 1]).id);
        });
        if (!gids.size) return;

        const parentOf = new Map(allGroups.map((g) => [g.id, g.parentId || null]));
        const elements = allEls.map((el) => (
            gids.has(el.groupId) ? { ...el, groupId: parentOf.get(el.groupId) || null } : el
        ));
        const groups = allGroups
            .filter((g) => !gids.has(g.id))
            .map((g) => (gids.has(g.parentId) ? { ...g, parentId: parentOf.get(g.parentId) || null } : g));
        set({
            document: { ...state.document, elements, groups },
            enteredGroupId: null,
            dirty: true,
            ...pushHistory(state),
        });
    },

    renameGroup(groupId, name) {
        const state = get();
        const groups = (state.document.groups || []).map((g) => (
            g.id === groupId ? { ...g, name: (name || '').trim() || g.name } : g
        ));
        set({ document: { ...state.document, groups }, dirty: true, ...pushHistory(state) });
    },

    /**
     * Apply a full layer restructure from the layers tree: new z-order
     * (back → front), per-element group membership, and optional group
     * re-parenting (dragging a whole sub-layer into / out of another).
     */
    restructureLayers(orderBackToFront, groupIdByEl = {}, parentByGroup = {}, frameByEl = {}) {
        const state = get();
        const map = Object.fromEntries(state.document.elements.map((e) => [e.id, e]));
        const placed = new Set();
        const elements = [];
        orderBackToFront.forEach((id) => {
            const el = map[id];
            if (!el || placed.has(id)) return;
            placed.add(id);
            const groupId = Object.prototype.hasOwnProperty.call(groupIdByEl, id)
                ? (groupIdByEl[id] || null)
                : (el.groupId || null);
            let moved = el;
            // Dragged into another frame's block in the layers panel: carry the
            // element ACROSS, keeping the same spot within the frame — otherwise it
            // would claim to belong to a frame it doesn't sit on, and the next drag
            // on canvas would silently send it back.
            const nextFrameId = frameByEl[id];
            if (nextFrameId && nextFrameId !== el.frameId) {
                const from = frameById(state.document, el.frameId);
                const to = frameById(state.document, nextFrameId);
                if (to) {
                    moved = {
                        ...el,
                        frameId: to.id,
                        x: el.x + (to.x - (from?.x ?? to.x)),
                        y: el.y + (to.y - (from?.y ?? to.y)),
                    };
                }
            }
            elements.push({ ...moved, zIndex: elements.length, groupId });
        });
        // keep any elements missing from the provided order
        getSortedElements(state.document.elements).forEach((el) => {
            if (placed.has(el.id)) return;
            elements.push({ ...el, zIndex: elements.length });
        });

        let groups = state.document.groups || [];
        if (Object.keys(parentByGroup).length) {
            groups = groups.map((g) => {
                if (!Object.prototype.hasOwnProperty.call(parentByGroup, g.id)) return g;
                const next = parentByGroup[g.id] || null;
                // Never let a group become its own descendant.
                if (next && getGroupSubtreeIds(groups, g.id).has(next)) return g;
                return { ...g, parentId: next };
            });
        }
        const pruned = pruneGroups(groups, elements);
        set({
            document: {
                ...state.document,
                groups: pruned.groups,
                elements: orderElementsByGroups(pruned.elements, pruned.groups)
                    .map((el, i) => ({ ...el, zIndex: i })),
            },
            dirty: true,
            ...pushHistory(state),
        });
    },

    addElement(type, overrides = {}) {
        // A document with no frames gets one first — see ensureFrame.
        const frame = get().ensureFrame();
        const state = get();
        const sorted = getSortedElements(state.document.elements);
        const zIndex = sorted.length ? Math.max(...sorted.map((e) => e.zIndex ?? 0)) + 1 : 0;
        const el = createElementByType(type, { ...overrides, zIndex, frameId: frame?.id || null });
        // center-ish placement in the ACTIVE frame, with a slight cascade
        const jitter = (sorted.length % 5) * 12;
        el.x = Math.round((frame?.x || 0) + ((frame?.width || 1050) - el.width) / 2 + jitter);
        el.y = Math.round((frame?.y || 0) + ((frame?.height || 600) - el.height) / 2 + jitter);
        const document = {
            ...state.document,
            elements: [...state.document.elements, el],
        };
        set({
            document,
            selectedIds: [el.id],
            selectedFrameId: null,
            dirty: true,
            tool: 'select',
            ...pushHistory(state),
        });
        return el;
    },

    /**
     * Drop a DRAWN element exactly where it was drawn.
     *
     * `addElement` centres what it creates and `addElements` lays a grid — both
     * wrong for the pen and the pencil, whose whole point is that the shape
     * belongs at the coordinates the hand just traced. The frame is the one the
     * drawing landed on, decided by geometry like every other placement.
     */
    addDrawnElement(spec) {
        if (!spec) return null;
        get().ensureFrame();
        const state = get();
        if (state.mode !== 'admin') return null;
        const frames = getFrames(state.document);
        const sorted = getSortedElements(state.document.elements);
        const zIndex = sorted.length ? Math.max(...sorted.map((e) => e.zIndex ?? 0)) + 1 : 0;
        const { styles, ...rest } = spec;
        const base = createElementByType(rest.type, { shape: rest.shape });
        const el = {
            ...base,
            ...rest,
            // `createElementByType` writes a full style sheet; the drawing only
            // means to override the few keys it named.
            styles: { ...base.styles, ...(styles || {}) },
            zIndex,
            frameId: frameForBox(frames, rest) || null,
        };
        set({
            document: { ...state.document, elements: [...state.document.elements, el] },
            selectedIds: [el.id],
            selectedFrameId: null,
            dirty: true,
            // A finished path hands the canvas back: the arrow returns and the new
            // path is selected, ready to be moved or restyled. Drawing another one
            // is one keystroke away (P / ⇧P) or one click on the toolbar — cheaper
            // than the stray extra path an armed pen leaves behind.
            tool: 'select',
            ...pushHistory(state),
        });
        return el;
    },

    /**
     * Add multiple elements in one undo step (e.g. multi-image upload).
     * `at` = a canvas point (drag-and-drop) → the grid is centered on the cursor
     * instead of on the artboard.
     */
    addElements(items = [], { at = null } = {}) {
        if (!items.length) return [];
        get().ensureFrame();
        const state = get();
        const frames = getFrames(state.document);
        const sorted = getSortedElements(state.document.elements);
        let zIndex = sorted.length ? Math.max(...sorted.map((e) => e.zIndex ?? 0)) + 1 : 0;
        const cols = Math.min(4, items.length);
        const gap = 16;
        // A drop lands in whatever frame is under the cursor; a plain insert goes to
        // the active one.
        const target = at ? frameById(state.document, frameForBox(frames, { x: at.x, y: at.y })) : null;
        const frame = target || state.activeFrame();
        const created = items.map((item, i) => {
            const { type, ...overrides } = item;
            const el = createElementByType(type, {
                ...overrides,
                zIndex: zIndex++,
                frameId: frame?.id || null,
            });
            const col = i % cols;
            const row = Math.floor(i / cols);
            const centerX = at ? at.x : (frame?.x || 0) + (frame?.width || 1050) / 2;
            const centerY = at ? at.y : (frame?.y || 0) + (frame?.height || 600) / 2 - 40;
            const baseX = Math.round(centerX - (el.width * cols + gap * (cols - 1)) / 2);
            const baseY = Math.round(centerY - el.height / 2);
            const jitter = at ? 0 : (sorted.length % 3) * 8;
            el.x = baseX + col * (el.width + gap) + jitter;
            el.y = baseY + row * (el.height + gap) + jitter;
            // A grid wider than the frame can push later items off it — settle each
            // one on the frame it actually landed on.
            el.frameId = frameForBox(frames, el, frame?.id || null);
            return el;
        });
        const document = {
            ...state.document,
            elements: [...state.document.elements, ...created],
        };
        set({
            document,
            selectedIds: created.map((e) => e.id),
            selectedFrameId: null,
            dirty: true,
            tool: 'select',
            ...pushHistory(state),
        });
        return created;
    },

    /**
     * Import pre-laid-out elements (from an AI/PDF/SVG artwork import) at their
     * EXACT x/y — unlike addElements, nothing is re-gridded or re-centred, so the
     * imported design keeps its layout. Optionally resizes the page to the artwork
     * in the same undo step.
     */
    importElements(items = [], canvasPatch = null, groups = [], { frameId = null } = {}) {
        if (!items.length && !canvasPatch) return [];
        get().ensureFrame();
        const state = get();
        const sorted = getSortedElements(state.document.elements);
        let zIndex = sorted.length ? Math.max(...sorted.map((e) => e.zIndex ?? 0)) + 1 : 0;
        // Importers lay artwork out from 0,0 (they only ever knew one artboard), so
        // everything shifts onto the target frame's origin.
        const frame = frameById(state.document, frameId) || state.activeFrame();
        const ox = frame?.x || 0;
        const oy = frame?.y || 0;
        const created = items.map((item) => {
            const { type, ...overrides } = item;
            // overrides carry explicit x/y/width/height/styles/groupId → factory keeps them as-is
            return createElementByType(type, {
                ...overrides,
                x: (Number(overrides.x) || 0) + ox,
                y: (Number(overrides.y) || 0) + oy,
                zIndex: zIndex++,
                frameId: frame?.id || null,
            });
        });
        // Imported layers → editor groups (registry entries). Members carry groupId
        // via their overrides; they stay contiguous in z-order so the layers tree is valid.
        const nextGroups = (groups && groups.length)
            ? [...(state.document.groups || []), ...groups.filter((g) => g && g.id)]
            : state.document.groups;
        // An import may resize the frame it lands on to the artwork's own size.
        let frames = getFrames(state.document);
        if (canvasPatch && frame) {
            frames = frames.map((f) => {
                if (f.id !== frame.id) return f;
                const next = { ...f, ...canvasPatch, x: f.x, y: f.y };
                if (canvasPatch.presetId == null && (canvasPatch.width != null || canvasPatch.height != null)) {
                    const match = SIZE_PRESETS.find(
                        (p) => p.id !== 'custom' && p.width === next.width && p.height === next.height
                    );
                    next.presetId = match?.id || 'custom';
                }
                return next;
            });
        }
        // Prune imported wrapper groups (single-child chains from Illustrator) and
        // re-flatten so every nested layer is contiguous — same treatment a saved
        // document gets on load, so the panel looks identical either way.
        const pruned = pruneGroups(nextGroups || [], [...state.document.elements, ...created]);
        const document = withFrames({
            ...state.document,
            groups: pruned.groups,
            elements: orderElementsByGroups(pruned.elements, pruned.groups)
                .map((el, i) => ({ ...el, zIndex: i })),
        }, frames);
        const liveIds = new Set(document.elements.map((e) => e.id));
        // The viewport re-fits off documentRevision, never off the frame size —
        // that is what stops a manual frame resize from throwing away the user's
        // zoom. So an import that RESIZES the frame has to ask for the fit here.
        // Dropping layers onto a frame that keeps its size must not (it would yank
        // the view away from wherever the user was working).
        const resized = !!frame && frames.some((f) => (
            f.id === frame.id && (f.width !== frame.width || f.height !== frame.height)
        ));
        set({
            document,
            selectedIds: created.map((e) => e.id).filter((id) => liveIds.has(id)),
            selectedFrameId: null,
            dirty: true,
            tool: 'select',
            ...(resized ? { documentRevision: (state.documentRevision || 0) + 1 } : null),
            ...pushHistory(state),
        });
        return created;
    },

    updateElement(id, patch, { skipHistory = false } = {}) {
        const state = get();
        if (state.mode === 'user') {
            const target = state.document.elements.find((e) => e.id === id);
            if (!target || target.locked || target.editableByUser === false) return;
            // The Text content lock is about the WORDS — and the Image lock about
            // the PICTURE: everything else about the layer stays the user's, so
            // each gates its own key rather than the whole write.
            if (writesSharedContent(target, patch)
                && isSectionLocked(target, contentLockKey(target), state.mode)) return;
        }
        // Typing into one member of a linked group types into all of them, and
        // dropping a photo on one of them fills every place it appears.
        const spread = spreadLinkedContent(state.document.elements, { [id]: patch });
        if (Object.keys(spread).length > 1) {
            get().updateElements(spread, { skipHistory });
            return;
        }
        const frames = getFrames(state.document);
        const spanMemo = { ...(state._spanMemo || {}) };
        const elements = state.document.elements.map((el) => {
            if (el.id !== id) return el;
            const { el: next, memo } = withRemappedColorSpans(el, patch, spanMemo[el.id]);
            if (memo) spanMemo[el.id] = memo; else delete spanMemo[el.id];
            if (patch.styles) next.styles = { ...(el.styles || {}), ...patch.styles };
            return reframe(frames, sizesText(patch) ? clampTextSize(next) : next, patch);
        });
        const document = { ...state.document, elements };
        if (skipHistory || state._historyGesture) {
            set({ document, dirty: true, _spanMemo: spanMemo });
        } else {
            set({
                document, dirty: true, _spanMemo: spanMemo, ...pushHistory(state),
            });
        }
    },

    updateElements(rawPatches, { skipHistory = false } = {}) {
        // patches: { [id]: partial }
        const state = get();
        const patches = spreadLinkedContent(state.document.elements, rawPatches || {});
        const frames = getFrames(state.document);
        const spanMemo = { ...(state._spanMemo || {}) };
        const elements = state.document.elements.map((el) => {
            const patch = patches[el.id];
            if (!patch) return el;
            // A mirrored member follows its group even when it is locked or not
            // itself user-editable: the admin linked it precisely so it would.
            const mirrored = !rawPatches?.[el.id] && writesSharedContent(el, patch);
            if (!mirrored && state.mode === 'user' && (el.locked || el.editableByUser === false)) return el;
            // A DIRECT content edit obeys its section lock — the words for text,
            // the picture for a photo; a mirrored one does not, for the same
            // reason it ignores the other locks — the admin linked these layers
            // precisely so they follow each other.
            if (!mirrored && writesSharedContent(el, patch)
                && isSectionLocked(el, contentLockKey(el), state.mode)) return el;
            const { el: next, memo } = withRemappedColorSpans(el, patch, spanMemo[el.id]);
            if (memo) spanMemo[el.id] = memo; else delete spanMemo[el.id];
            if (patch.styles) next.styles = { ...(el.styles || {}), ...patch.styles };
            return reframe(frames, sizesText(patch) ? clampTextSize(next) : next, patch);
        });
        const document = { ...state.document, elements };
        if (skipHistory || state._historyGesture) set({ document, dirty: true, _spanMemo: spanMemo });
        else {
            set({
                document, dirty: true, _spanMemo: spanMemo, ...pushHistory(state),
            });
        }
    },

    /**
     * Tie layers together so they always show the same thing — the same words,
     * or the same photo.
     *
     * Nothing on the artboard moves when the link is made: every layer keeps the
     * wording and the picture it was designed with. The link is about the fill-in
     * step — one field for the user, and whatever they type or upload lands in
     * every place at once. Mixed selections link only their leading kind — a photo
     * cannot follow a text field.
     */
    linkContent(ids = [], label = '') {
        const state = get();
        const chosen = state.document.elements.filter(
            (el) => ids.includes(el.id) && linkKind(el),
        );
        const kind = linkKind(chosen[0]);
        const targets = chosen.filter((el) => linkKind(el) === kind);
        if (targets.length < 2) return null;
        const key = createId('link');
        const name = String(label || '').trim() || targets[0].name
            || (kind === 'image' ? 'Linked photo' : kind === 'table' ? 'Linked table' : 'Linked text');
        const patches = {};
        // Linking CHANGES NOTHING on the artboard. The layers keep the words and
        // the pictures the admin placed there — those are the design, and the
        // sample face on the front of a card is not a mistake to be corrected by
        // the one on the back. The link only says what happens NEXT: from the
        // first edit onwards they move together.
        targets.forEach((el) => {
            patches[el.id] = { contentKey: key, contentLabel: name, contentDetached: false };
        });
        get().updateElements(patches);
        return key;
    },

    /** Rename a group — the caption the user reads above the field. */
    renameContentGroup(key, label) {
        const state = get();
        const name = String(label || '').trim();
        if (!key || !name) return;
        const patches = {};
        state.document.elements.forEach((el) => {
            if (el.contentKey === key) patches[el.id] = { contentLabel: name };
        });
        if (Object.keys(patches).length) get().updateElements(patches);
    },

    /** Break the link entirely — each layer goes back to its own words. */
    unlinkContent(ids = []) {
        const state = get();
        const patches = {};
        state.document.elements.forEach((el) => {
            if (!ids.includes(el.id) || !el.contentKey) return;
            patches[el.id] = { contentKey: null, contentLabel: null, contentDetached: false };
        });
        if (Object.keys(patches).length) get().updateElements(patches);
    },

    /**
     * Let one place say something different — or put it back in step.
     *
     * Re-joining copies the group's current wording — or its photo — onto the
     * returning member, because a field that says one thing while its layer says
     * another is the whole bug this feature exists to avoid.
     */
    setContentDetached(id, detached) {
        const state = get();
        const el = state.document.elements.find((e) => e.id === id);
        if (!el?.contentKey) return;
        if (detached) {
            get().updateElements({ [id]: { contentDetached: true } });
            return;
        }
        const lead = state.document.elements.find(
            (e) => e.contentKey === el.contentKey && e.id !== id && !e.contentDetached,
        );
        // The returning layer takes the group's picture, so the crop it kept while
        // it was on its own frames a photo it no longer shows. A code takes the
        // value back, plus the logo when the lead is a code too; a table takes
        // the grid.
        const shared = !lead ? null
            : el.type === ELEMENT_TYPES.IMAGE ? { src: lead.src, crop: null }
                : el.type === ELEMENT_TYPES.TABLE
                    ? (lead.type === ELEMENT_TYPES.TABLE
                        ? { cells: cloneCells(lead.cells), rows: lead.rows, cols: lead.cols }
                        : null)
                    : el.type === ELEMENT_TYPES.QRCODE && lead.type === ELEMENT_TYPES.QRCODE
                        ? { content: lead.content, src: lead.src }
                        : { content: lead.content };
        get().updateElements({ [id]: { contentDetached: false, ...shared } });
    },

    deleteSelected() {
        const state = get();
        // A selected FRAME deletes as a whole, contents included — Delete on a frame
        // meaning "delete the frame" is what every design tool does.
        if (state.selectedFrameId) {
            get().deleteFrame(state.selectedFrameId);
            return;
        }
        if (!state.selectedIds.length) return;
        const elements = state.document.elements.filter(
            (el) => !state.selectedIds.includes(el.id) || isLockedFor(el, state.mode)
        );
        const pruned = pruneGroups(state.document.groups || [], elements);
        set({
            document: { ...state.document, elements: pruned.elements, groups: pruned.groups },
            selectedIds: [],
            enteredGroupId: null,
            croppingId: null,
            cropSeed: null,
            dirty: true,
            ...pushHistory(state),
        });
    },

    duplicateSelected() {
        const state = get();
        const selected = getSortedElements(state.document.elements)
            .filter((el) => state.selectedIds.includes(el.id));
        if (!selected.length) return;
        // Groups duplicated whole (≥2 selected members) get a fresh group id; one
        // member copied on its own stays in the group it came from.
        const { remap, newGroups } = remapGroupsForCopies(selected, state.document.groups);
        const clones = selected.map((el) => ({
            ...clone(el),
            id: createId('el'),
            x: el.x + 16,
            y: el.y + 16,
            name: `${el.name || el.type} copy`,
            groupId: remap[el.groupId] || el.groupId || null,
        }));
        set({
            document: {
                ...state.document,
                elements: insertClonesAbove(state.document.elements, selected, clones),
                groups: [...(state.document.groups || []), ...newGroups],
            },
            selectedIds: clones.map((c) => c.id),
            dirty: true,
            ...pushHistory(state),
        });
    },

    /**
     * Copy elements WHERE THEY STAND and select the copies — the first half of the
     * ⌥-drag gesture every design tool has (Illustrator, Photoshop, Figma): the
     * original stays put and the copy is what the pointer carries off.
     *
     * Returns `{ oldId: newId }` so the caller can keep dragging the same boxes, and
     * takes `skipHistory` because the whole gesture is ONE undo step, committed when
     * the pointer comes up.
     */
    duplicateInPlace(ids = null, { skipHistory = false } = {}) {
        const state = get();
        // End users get a fixed template to fill in — duplicating layers is an
        // admin gesture, same rule addElement/duplicateSelected follow.
        if (state.mode === 'user') return {};
        const wanted = ids && ids.length ? ids : state.selectedIds;
        const selected = getSortedElements(state.document.elements)
            .filter((el) => wanted.includes(el.id));
        if (!selected.length) return {};
        const { remap, newGroups } = remapGroupsForCopies(selected, state.document.groups);
        const clones = selected.map((el) => ({
            ...clone(el),
            id: createId('el'),
            name: `${el.name || el.type} copy`,
            groupId: remap[el.groupId] || el.groupId || null,
        }));
        set({
            document: {
                ...state.document,
                elements: insertClonesAbove(state.document.elements, selected, clones),
                groups: [...(state.document.groups || []), ...newGroups],
            },
            selectedIds: clones.map((c) => c.id),
            dirty: true,
            ...(skipHistory ? null : pushHistory(state)),
        });
        const map = {};
        selected.forEach((el, i) => { map[el.id] = clones[i].id; });
        return map;
    },

    copySelected() {
        const state = get();
        const selected = state.document.elements
            .filter((el) => state.selectedIds.includes(el.id))
            .map((el) => clone(el));
        if (!selected.length) return false;
        set({ clipboard: selected, pasteCount: 0 });
        try {
            // Belt and braces. The canvas's `copy` handler is what actually claims
            // the system clipboard (it works everywhere); this covers the case
            // where no copy event fires, and needs a secure context to do anything.
            void navigator.clipboard?.writeText?.(encodeClipboard(selected))?.catch?.(() => {});
        } catch {
            // ignore — in-app clipboard still works
        }
        return true;
    },

    /**
     * Cut the subject out of one image layer.
     *
     * Lives in the STORE, not in the panel that starts it: the model download and
     * the inference take a while, and selecting another layer unmounts the image
     * section. Held in component state, the progress vanished the moment you
     * clicked away and came back reading "Remove background" while the job was
     * still running — so it is keyed by element id here, where every mount of the
     * panel can find it again.
     */
    async removeElementBackground(id) {
        const el = get().document.elements.find((e) => e.id === id);
        if (!el?.src || get().bgRemoval[id]?.busy) return;

        const patch = (value) => set((s) => ({
            bgRemoval: value
                ? { ...s.bgRemoval, [id]: { ...(s.bgRemoval[id] || {}), ...value } }
                : Object.fromEntries(Object.entries(s.bgRemoval).filter(([key]) => key !== id)),
        }));

        patch({ busy: true, pct: 0, error: '' });
        try {
            const src = await removeImageBackground(el.src, (pct) => patch({ pct }));
            // The cutout has the source's pixel dimensions, so the box the user
            // sized — and any crop framing it — still describes this picture.
            get().updateElement(id, { src });
            patch(null);
        } catch (err) {
            patch({ busy: false, error: err?.message || 'Background removal failed.' });
        }
    },

    /** Dismiss a failed cut-out's message (the panel's × / a retry). */
    clearBackgroundRemoval(id) {
        set((s) => ({
            bgRemoval: Object.fromEntries(Object.entries(s.bgRemoval).filter(([key]) => key !== id)),
        }));
    },

    /**
     * Shrink one image layer's picture — see utils/imageOptimize for what that
     * means and when it declines to.
     *
     * Kept in the store for the same reason the cut-out is: the work is slow
     * enough to click away from, and the RESULT has to survive that too — the
     * panel shows what was saved, and the way back, and neither can live in a
     * component that unmounts the moment another layer is selected.
     *
     * The original is remembered off-document. It is the undo for this one act,
     * not a second copy of the photo to save, upload and carry around forever;
     * plain undo covers it as well, and both of those are session-lived — which
     * is the point of optimising, since the smaller picture is what should be
     * kept.
     */
    async optimizeElementImage(id) {
        const el = get().document.elements.find((e) => e.id === id);
        if (!el?.src || get().imageOptimize[id]?.busy) return;

        const patch = (value) => set((s) => ({
            imageOptimize: value
                ? { ...s.imageOptimize, [id]: { ...(s.imageOptimize[id] || {}), ...value } }
                : Object.fromEntries(Object.entries(s.imageOptimize).filter(([key]) => key !== id)),
        }));

        patch({ busy: true, error: '', done: false });
        try {
            const result = await optimizeImageSrc(el.src, { maxSide: targetSideFor(el) });
            if (!result) {
                patch({ busy: false, done: false, error: 'Already as small as it can usefully get.' });
                return;
            }
            set((s) => ({
                // Only the FIRST original is kept: optimising twice must still be
                // able to go back to the photo that was uploaded, not to the
                // half-way version this one started from.
                imageOriginals: s.imageOriginals[id]
                    ? s.imageOriginals
                    : { ...s.imageOriginals, [id]: { src: el.src, bytes: result.before } },
            }));
            // The crop is stored as fractions of the picture, so a smaller picture
            // is still framed exactly the same way.
            get().updateElement(id, { src: result.src });
            patch({
                busy: false,
                done: true,
                error: '',
                before: result.before ?? get().imageOriginals[id]?.bytes ?? null,
                after: result.bytes,
                width: result.width,
                height: result.height,
                naturalWidth: result.naturalWidth,
                naturalHeight: result.naturalHeight,
                resized: result.resized,
            });
        } catch (err) {
            patch({ busy: false, error: err?.message || 'Could not optimise that image.' });
        }
    },

    /** Put the picture back the way it was uploaded, and forget the whole thing. */
    revertElementImage(id) {
        const original = get().imageOriginals[id];
        if (!original?.src) return;
        get().updateElement(id, { src: original.src });
        set((s) => ({
            imageOriginals: Object.fromEntries(Object.entries(s.imageOriginals).filter(([k]) => k !== id)),
            imageOptimize: Object.fromEntries(Object.entries(s.imageOptimize).filter(([k]) => k !== id)),
        }));
    },

    /** Dismiss the panel's result or its error, leaving the picture alone. */
    clearImageOptimize(id) {
        set((s) => ({
            imageOptimize: Object.fromEntries(Object.entries(s.imageOptimize).filter(([k]) => k !== id)),
        }));
    },

    /**
     * Take on the R2 URLs the API returned for images that were sent embedded.
     *
     * Kept out of the undo history and off the dirty flag on purpose: the
     * picture has not changed, only how it is addressed. An element is only
     * rewritten when it still holds the exact data URL that was sent, so an
     * image swapped while the save was in flight is left alone.
     */
    adoptMediaUrls(sentElements = [], savedElements = []) {
        if (!Array.isArray(sentElements) || !Array.isArray(savedElements)) return;
        const lifted = new Map();
        const savedById = new Map(savedElements.map((el) => [el?.id, el?.src]));
        sentElements.forEach((el) => {
            const sent = el?.src;
            const saved = savedById.get(el?.id);
            if (sent && saved && saved !== sent && String(sent).startsWith('data:')) {
                lifted.set(el.id, { sent, saved });
            }
        });
        if (!lifted.size) return;

        const state = get();
        let changed = false;
        const elements = state.document.elements.map((el) => {
            const entry = lifted.get(el.id);
            if (!entry || el.src !== entry.sent) return el;
            changed = true;
            return { ...el, src: entry.saved };
        });
        if (changed) set({ document: { ...state.document, elements } });
    },

    /** The copied elements as clipboard text, or '' when nothing is copied. */
    serializeClipboard() {
        const items = get().clipboard;
        return items?.length ? encodeClipboard(items) : '';
    },

    cutSelected() {
        const state = get();
        if (!state.selectedIds.length) return false;
        get().copySelected();
        get().deleteSelected();
        return true;
    },

    /**
     * Paste elements.
     *
     * `systemText` is the text flavour of the system clipboard, passed in by the
     * canvas's paste handler. Elements decoded from it win when they are a
     * different set than the one ⌘C last put in the in-app clipboard — that is
     * how a copy made before a reload, or in another tab, still pastes. Pasting
     * the same set again keeps cascading 16px per repeat.
     */
    /**
     * Plain text from ANOTHER app becomes a text layer, the way a pasted image
     * becomes an image layer. Without this, Cmd/Ctrl+V on the canvas did nothing
     * at all unless a text box was already open and focused — so the only way to
     * get copied words onto a template was to insert a text box, double-click
     * into it and paste there.
     */
    pasteText(text, { at = null } = {}) {
        const raw = String(text || '').replace(/\r\n/g, '\n');
        if (!raw.trim()) return false;
        const frame = get().ensureFrame();
        const state = get();
        // Creating layers is an admin gesture, the same rule addElement follows.
        if (state.mode !== 'admin') return false;

        const sorted = getSortedElements(state.document.elements);
        const zIndex = sorted.length ? Math.max(...sorted.map((e) => e.zIndex ?? 0)) + 1 : 0;
        // A column, not one very long line: pasted prose is a paragraph, so the
        // box keeps a readable width and grows DOWN as it wraps.
        const width = Math.round(Math.max(160, Math.min(360, (frame?.width || 600) - 80)));
        const el = createElementByType(ELEMENT_TYPES.TEXT, {
            content: raw,
            // The layer list reads better with the words in it than with thirty
            // rows all called "Text".
            name: (raw.split('\n').find((line) => line.trim()) || 'Text').trim().slice(0, 40),
            textAutoSize: 'height',
            width,
            zIndex,
            frameId: frame?.id || null,
        });
        // Measured with the same probe the editor uses for autosize, so the box
        // that appears is the box the text actually needs.
        const box = minTextBox(el, el.styles, width);
        el.height = Math.max(1, Math.round(box?.height || el.height));

        const cascade = (sorted.length % 5) * 12;
        el.x = Math.round(at
            ? at.x - width / 2
            : (frame?.x || 0) + ((frame?.width || 1050) - width) / 2 + cascade);
        el.y = Math.round(at
            ? at.y - el.height / 2
            : (frame?.y || 0) + ((frame?.height || 600) - el.height) / 2 + cascade);

        set({
            document: { ...state.document, elements: [...state.document.elements, el] },
            selectedIds: [el.id],
            selectedFrameId: null,
            dirty: true,
            tool: 'select',
            ...pushHistory(state),
        });
        return true;
    },

    pasteClipboard({ systemText = '', at = null } = {}) {
        const state = get();
        const external = decodeClipboard(systemText);
        // Text the editor did not write is a paste of ITS OWN, and it is newer
        // than whatever is in the in-app clipboard — so it wins, exactly as a
        // pasted image does.
        if (!external && String(systemText || '').trim()) {
            return get().pasteText(systemText, { at });
        }
        const isNewSource = !!external && !sameClipboardSource(external, state.clipboard);
        const items = isNewSource ? external : (state.clipboard.length ? state.clipboard : external);
        if (!items?.length) return false;
        const pasteCount = isNewSource ? 0 : (state.pasteCount || 0);
        const offset = 16 * (pasteCount + 1);
        const maxZ = state.document.elements.length
            ? Math.max(...state.document.elements.map((e) => e.zIndex ?? 0))
            : 0;
        // "Paste here" (the right-click menu) lands the copied set on the pointer:
        // its bounding box is centred there instead of cascading off the original.
        let dx = offset;
        let dy = offset;
        if (at) {
            const left = Math.min(...items.map((el) => el.x || 0));
            const top = Math.min(...items.map((el) => el.y || 0));
            const right = Math.max(...items.map((el) => (el.x || 0) + (el.width || 0)));
            const bottom = Math.max(...items.map((el) => (el.y || 0) + (el.height || 0)));
            dx = Math.round(at.x - (left + right) / 2);
            dy = Math.round(at.y - (top + bottom) / 2);
        }
        // Copied group members stay grouped — under a fresh group id
        const { remap, newGroups } = remapGroupsForCopies(items, state.document.groups);
        const frames = getFrames(state.document);
        const clones = items.map((el, i) => {
            const x = (el.x || 0) + dx;
            const y = (el.y || 0) + dy;
            return {
                ...clone(el),
                id: createId('el'),
                x,
                y,
                zIndex: maxZ + 1 + i,
                name: el.name || el.type,
                groupId: remap[el.groupId] || null,
                // Pasting into a different artboard has to re-home the copy, or it
                // would export with the frame it was copied from.
                frameId: frameForBox(frames, { x, y, width: el.width, height: el.height })?.id
                    ?? el.frameId ?? null,
            };
        });
        set({
            document: {
                ...state.document,
                elements: [...state.document.elements, ...clones],
                groups: [...(state.document.groups || []), ...newGroups],
            },
            selectedIds: clones.map((c) => c.id),
            clipboard: items,
            pasteCount: pasteCount + 1,
            dirty: true,
            tool: 'select',
            ...pushHistory(state),
        });
        return true;
    },

    bringForward(id) {
        const state = get();
        const sorted = getSortedElements(state.document.elements);
        const idx = sorted.findIndex((e) => e.id === id);
        if (idx < 0 || idx === sorted.length - 1) return;
        const a = sorted[idx];
        const b = sorted[idx + 1];
        const elements = state.document.elements.map((el) => {
            if (el.id === a.id) return { ...el, zIndex: b.zIndex ?? idx + 1 };
            if (el.id === b.id) return { ...el, zIndex: a.zIndex ?? idx };
            return el;
        });
        set({ document: { ...state.document, elements }, dirty: true, ...pushHistory(state) });
    },

    sendBackward(id) {
        const state = get();
        const sorted = getSortedElements(state.document.elements);
        const idx = sorted.findIndex((e) => e.id === id);
        if (idx <= 0) return;
        const a = sorted[idx];
        const b = sorted[idx - 1];
        const elements = state.document.elements.map((el) => {
            if (el.id === a.id) return { ...el, zIndex: b.zIndex ?? idx - 1 };
            if (el.id === b.id) return { ...el, zIndex: a.zIndex ?? idx };
            return el;
        });
        set({ document: { ...state.document, elements }, dirty: true, ...pushHistory(state) });
    },

    /**
     * All the way front / back, for the whole selection at once.
     *
     * z-order is re-numbered from the sorted list rather than by pushing the
     * moved layers past the maximum: that keeps `orderElementsByGroups`'s
     * contiguity guarantee (every group stays one z-block) intact, because the
     * layer list is rebuilt through the same path a drag uses.
     */
    bringToFront(ids = get().selectedIds) {
        const state = get();
        const moving = new Set(ids);
        if (!moving.size) return;
        const sorted = getSortedElements(state.document.elements);
        const order = [...sorted.filter((e) => !moving.has(e.id)), ...sorted.filter((e) => moving.has(e.id))];
        get().reorderLayers(order.map((e) => e.id));
    },

    sendToBack(ids = get().selectedIds) {
        const state = get();
        const moving = new Set(ids);
        if (!moving.size) return;
        const sorted = getSortedElements(state.document.elements);
        const order = [...sorted.filter((e) => moving.has(e.id)), ...sorted.filter((e) => !moving.has(e.id))];
        get().reorderLayers(order.map((e) => e.id));
    },

    /**
     * Show / hide and lock / unlock a whole selection — one undo step, and the
     * flag is FLIPPED from the first element so a mixed selection lands on one
     * state instead of each item toggling its own way.
     */
    toggleVisibility(ids = get().selectedIds) {
        const state = get();
        if (!ids.length) return;
        const first = state.document.elements.find((e) => e.id === ids[0]);
        const visible = !(first?.visible !== false);
        get().updateElements(Object.fromEntries(ids.map((id) => [id, { visible }])));
    },

    /**
     * The ADMIN lock — pins the layer inside this editor. Both locks are the
     * admin's to set: a user has no lock of their own to toggle, and letting one
     * set the user lock would leave them stuck behind it (see isLockedFor).
     */
    toggleLock(ids = get().selectedIds) {
        const state = get();
        if (state.mode !== 'admin' || !ids.length) return;
        const first = state.document.elements.find((e) => e.id === ids[0]);
        const adminLocked = !first?.adminLocked;
        get().updateElements(Object.fromEntries(ids.map((id) => [id, { adminLocked }])));
    },

    /** The USER lock — what the person filling this template in may not touch. */
    toggleUserLock(ids = get().selectedIds) {
        const state = get();
        if (state.mode !== 'admin' || !ids.length) return;
        const first = state.document.elements.find((e) => e.id === ids[0]);
        const locked = !first?.locked;
        get().updateElements(Object.fromEntries(ids.map((id) => [id, { locked }])));
    },

    /**
     * Mirror the selection. `flipX` / `flipY` are element flags, not a negative
     * scale on the box: the geometry (x/y/width/height, and every snap and
     * handle that reads it) stays exactly as it was, and only the paint is
     * mirrored — which is what both renderers can honour identically.
     */
    flipSelected(axis, ids = get().selectedIds) {
        const state = get();
        if (!ids.length) return;
        const key = axis === 'y' ? 'flipY' : 'flipX';
        const patches = {};
        ids.forEach((id) => {
            const el = state.document.elements.find((e) => e.id === id);
            if (el) patches[id] = { [key]: !el[key] };
        });
        get().updateElements(patches);
    },

    /**
     * Use the LOWEST selected layer as a mask for the ones above it — Figma's
     * rule, and the one users expect: the mask's silhouette is what shows.
     *
     * Needs at least two layers. The mask stops painting itself (see
     * utils/elementMask); everything above takes `maskId`.
     *
     * Like Figma, a mask made from loose layers wraps them in a group first, so
     * the layers list shows the mask owning exactly what it clips — and so
     * moving the mask moves its subjects. A selection that already IS one group
     * is left where it is rather than nested a second time. Grouping pushes the
     * history entry and the mask patch rides along with it: one undo, one act.
     */
    maskSelected(ids = get().selectedIds) {
        const state = get();
        const chosen = getSortedElements(state.document.elements).filter((e) => ids.includes(e.id));
        if (chosen.length < 2) return false;

        const gid = chosen[0].groupId || null;
        const ownGroup = !!gid
            && chosen.every((e) => e.groupId === gid)
            && getGroupElements(state.document.elements, state.document.groups || [], gid)
                .every((m) => ids.includes(m.id));
        // "Masked Group 1", "Masked Group 2", … — numbered past whatever masked
        // groups already exist, so a renamed or deleted one never steals a name.
        const used = (state.document.groups || [])
            .map((g) => /^Masked Group (\d+)$/.exec(g.name || '')?.[1])
            .filter(Boolean)
            .map(Number);
        const maskName = `Masked Group ${used.length ? Math.max(...used) + 1 : 1}`;
        const grouped = !ownGroup && !!get().groupSelected(ids, { name: maskName });

        const mask = chosen[0];
        const patches = { [mask.id]: { isMask: true, maskId: null } };
        chosen.slice(1).forEach((el) => { patches[el.id] = { maskId: mask.id, isMask: false }; });
        get().updateElements(patches, { skipHistory: grouped });
        return true;
    },

    /**
     * Undo a mask. Releasing the MASK frees every layer it clips; releasing a
     * masked layer frees just that one, and the mask goes back to painting once
     * nothing points at it any more.
     */
    releaseMask(ids = get().selectedIds) {
        const state = get();
        const patches = {};
        const freed = new Set();
        ids.forEach((id) => {
            const el = state.document.elements.find((e) => e.id === id);
            if (!el) return;
            if (el.isMask) {
                patches[el.id] = { isMask: false };
                state.document.elements.forEach((other) => {
                    if (other.maskId !== el.id) return;
                    patches[other.id] = { maskId: null };
                    freed.add(other.id);
                });
            } else if (el.maskId) {
                patches[el.id] = { maskId: null };
                freed.add(el.id);
            }
        });
        if (!Object.keys(patches).length) return;
        // A mask left with nothing pointing at it is a plain layer again.
        state.document.elements.forEach((mask) => {
            if (!mask.isMask || patches[mask.id]) return;
            const stillUsed = state.document.elements
                .some((o) => o.maskId === mask.id && !freed.has(o.id));
            if (!stillUsed) patches[mask.id] = { isMask: false };
        });
        get().updateElements(patches);
        // Rides on the history entry the patches just pushed: releasing a mask is
        // one act, and one undo has to put both the link and the name back.
        get().renameFreedMaskGroups({ skipHistory: true });
    },

    /**
     * "Masked Group 3" holding nothing masked is a lie the layers list keeps
     * telling — the wrapper was named after the act, and releasing undoes the act.
     *
     * Only the name masking WROTE is taken back: a group the user renamed keeps
     * whatever they called it, mask or no mask. The replacement follows the plain
     * numbering new groups get, so a released one is indistinguishable from a
     * group made by hand, which is exactly what it now is.
     */
    renameFreedMaskGroups({ skipHistory = false } = {}) {
        const state = get();
        const groups = state.document.groups || [];
        if (!groups.length) return;
        const auto = /^Masked Group \d+$/;
        const used = new Set(groups.map((g) => g.name));
        let next = 1;
        const freeName = () => {
            while (used.has(`Group ${next}`)) next += 1;
            const name = `Group ${next}`;
            used.add(name);
            return name;
        };

        let changed = false;
        const renamed = groups.map((g) => {
            if (!auto.test(g.name || '')) return g;
            const members = getGroupElements(state.document.elements, groups, g.id);
            if (members.some((m) => m.isMask || m.maskId)) return g;
            changed = true;
            used.delete(g.name);
            return { ...g, name: freeName() };
        });
        if (!changed) return;
        set({
            document: { ...state.document, groups: renamed },
            dirty: true,
            ...(skipHistory || state._historyGesture ? null : pushHistory(state)),
        });
    },

    reorderLayers(orderedIds) {
        const state = get();
        const map = Object.fromEntries(state.document.elements.map((e) => [e.id, e]));
        const elements = orderedIds.map((id, i) => ({ ...map[id], zIndex: i })).filter(Boolean);
        // keep any missing
        state.document.elements.forEach((el) => {
            if (!orderedIds.includes(el.id)) elements.push(el);
        });
        set({ document: { ...state.document, elements }, dirty: true, ...pushHistory(state) });
    },

    nudge(dx, dy) {
        const state = get();
        // Arrow keys move whatever is selected — a frame moves with its contents.
        if (state.selectedFrameId) {
            get().moveFrame(state.selectedFrameId, dx, dy);
            return;
        }
        if (!state.selectedIds.length) return;
        const patches = {};
        state.selectedIds.forEach((id) => {
            const el = state.document.elements.find((e) => e.id === id);
            if (!el || isLockedFor(el, state.mode)) return;
            // Arrow keys are the Position section by another route.
            if (isSectionLocked(el, 'position', state.mode)) return;
            patches[id] = { x: el.x + dx, y: el.y + dy };
        });
        get().updateElements(patches);
    },

    alignSelected(alignment) {
        const state = get();
        const selected = state.document.elements.filter(
            (e) => state.selectedIds.includes(e.id)
                && !isLockedFor(e, state.mode)
                && !isSectionLocked(e, 'position', state.mode)
        );
        if (!selected.length) return;

        /** One element on its own aligns to ITS OWN frame, not to some global page. */
        const toFrame = (el, patches) => {
            const frame = frameById(state.document, el.frameId) || getFrames(state.document)[0];
            if (!frame) return;
            const { x, y, width, height } = frame;
            if (alignment === 'left') patches[el.id] = { x };
            if (alignment === 'right') patches[el.id] = { x: x + width - el.width };
            if (alignment === 'center') patches[el.id] = { x: x + (width - el.width) / 2 };
            if (alignment === 'top') patches[el.id] = { y };
            if (alignment === 'bottom') patches[el.id] = { y: y + height - el.height };
            if (alignment === 'middle') patches[el.id] = { y: y + (height - el.height) / 2 };
        };

        // Align INSIDE each artboard. A selection that spans frames — the same
        // layer picked on two copies of a card, say — must not be dragged onto one
        // shared edge: each frame's share of the selection lines up on that frame.
        const byFrame = new Map();
        selected.forEach((el) => {
            const key = el.frameId || '';
            if (!byFrame.has(key)) byFrame.set(key, []);
            byFrame.get(key).push(el);
        });

        const patches = {};
        byFrame.forEach((group) => {
            if (group.length < 2) {
                toFrame(group[0], patches);
                return;
            }
            const minX = Math.min(...group.map((e) => e.x));
            const maxX = Math.max(...group.map((e) => e.x + e.width));
            const minY = Math.min(...group.map((e) => e.y));
            const maxY = Math.max(...group.map((e) => e.y + e.height));
            const cx = (minX + maxX) / 2;
            const cy = (minY + maxY) / 2;
            group.forEach((el) => {
                if (alignment === 'left') patches[el.id] = { x: minX };
                if (alignment === 'right') patches[el.id] = { x: maxX - el.width };
                if (alignment === 'center') patches[el.id] = { x: cx - el.width / 2 };
                if (alignment === 'top') patches[el.id] = { y: minY };
                if (alignment === 'bottom') patches[el.id] = { y: maxY - el.height };
                if (alignment === 'middle') patches[el.id] = { y: cy - el.height / 2 };
            });
        });
        if (Object.keys(patches).length) get().updateElements(patches);
    },

    snapValue(value, guides = []) {
        if (!get().snapEnabled) return value;
        for (const g of guides) {
            if (Math.abs(value - g) <= SNAP_THRESHOLD) return g;
        }
        return value;
    },

    undo() {
        const state = get();
        if (!state.past.length) return;
        const previous = state.past[state.past.length - 1];
        const past = state.past.slice(0, -1);
        set({
            past,
            future: [clone(state.document), ...state.future].slice(0, MAX_HISTORY),
            document: previous,
            dirty: true,
            selectedIds: [],
            // Parked runs belong to the text that was being typed; the document
            // stepping back under them makes them a lie.
            _spanMemo: {},
            // The overlay reads a crop out of the document — stepping the document
            // out from under it would leave it pointing at a rect that is gone.
            croppingId: null,
            cropSeed: null,
            _historyGesture: false,
        });
    },

    redo() {
        const state = get();
        if (!state.future.length) return;
        const next = state.future[0];
        set({
            future: state.future.slice(1),
            past: [...state.past, clone(state.document)].slice(-MAX_HISTORY),
            document: next,
            dirty: true,
            selectedIds: [],
            _spanMemo: {},
            croppingId: null,
            cropSeed: null,
            _historyGesture: false,
        });
    },

    /**
     * Start a multi-update gesture (slider drag).
     * Snapshot is committed on endHistoryGesture only if the document actually changed.
     */
    beginHistoryGesture() {
        const state = get();
        if (state._historyGesture) return;
        set({
            _historyGesture: true,
            _historyGestureBase: clone(state.document),
        });
    },

    endHistoryGesture() {
        const state = get();
        if (!state._historyGesture) return;
        const base = state._historyGestureBase;
        let past = state.past;
        let future = state.future;
        let changed = false;
        try {
            changed = !!(base && JSON.stringify(state.document) !== JSON.stringify(base));
        } catch {
            changed = !!base;
        }
        if (changed && base) {
            past = [...state.past, base].slice(-MAX_HISTORY);
            future = [];
        }
        set({
            _historyGesture: false,
            _historyGestureBase: null,
            past,
            future,
            dirty: state.dirty || changed,
        });
    },

    /**
     * Commit an earlier document snapshot as a single undo step (after a finished drag).
     * Prefer beginHistoryGesture for new code; this keeps drag pointer-up paths explicit.
     */
    commitHistorySnapshot(snapshot) {
        if (!snapshot) return;
        const state = get();
        try {
            if (JSON.stringify(state.document) === JSON.stringify(snapshot)) return;
        } catch {
            // compare failed — still commit
        }
        set({
            past: [...state.past, snapshot].slice(-MAX_HISTORY),
            future: [],
            dirty: true,
            _historyGesture: false,
        });
    },

    markClean() {
        set({ dirty: false });
    },

    getDocument() {
        return get().document;
    },
}));
